import { FAST_API_URL, postOptions } from "../index";

/**
 * «Без автоправок — только ручная работа»: флаг пары (медиа + исходный → целевой язык).
 *
 * Лежит на сервере, в папке пары (`subs/<src>_<tgt>/.manual_only.json`), а не в браузере: его
 * читают и сервер редактора, и сборка финального видео. Переозвучка в такой паре никогда не
 * подгоняет скорость и ничего не двигает раньше; соседей, на которых налез новый дубль, она
 * отодвигает позже — на его конец + 50 мс (правило владельца 01.10.2026: наложений не бывает ни в
 * каком режиме). Флаг, живущий только во вкладке переводчика, защищал бы одну вкладку, а лекцию
 * портил бы тот же сервер, что и раньше.
 */
export interface ManualOnlyState {
    manual_only: boolean;
    set_by?: string;
    set_at?: string;
    reason?: string;
}

interface ManualOnlyPairArgs {
    mediaId: string;
    /** Целевой язык пары — в любом виде, который сервер понимает (en, en-US, english). */
    lang: string;
    /** Исходный язык пары; сервер по умолчанию считает его русским. */
    languageFrom?: string;
}

/**
 * Корень сервера редактора. У эфира FAST_API_URL указывает на `/stream_editor/v1` — там
 * собраны только эфирные двойники ручек, а флаг пары один на лекцию и на эфир и живёт в
 * корне того же сервера.
 */
const editorBackendRoot = () => FAST_API_URL.replace(/\/stream_editor\/v1\/?$/, "");

const pairQuery = ({ mediaId, lang, languageFrom }: ManualOnlyPairArgs) =>
    new URLSearchParams({ media: mediaId, lang, language_from: languageFrom || "ru" }).toString();

/** Включён только настоящий `true`: так же решает сервер — всё прочее значит «выключено». */
export const normalizeManualOnlyState = (raw: unknown): ManualOnlyState => {
    const data = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    return {
        manual_only: data.manual_only === true,
        ...(typeof data.set_by === "string" ? { set_by: data.set_by } : {}),
        ...(typeof data.set_at === "string" ? { set_at: data.set_at } : {}),
        ...(typeof data.reason === "string" ? { reason: data.reason } : {}),
    };
};

const failure = async (response: Response, fallback: string) => {
    const text = await response.text().catch(() => "");
    try {
        const detail = JSON.parse(text)?.detail;
        if (typeof detail === "string" && detail) return new Error(detail);
        // Отказ с подробностями (`{"detail": {"message": …, …}}`) — показываем его слова, а не JSON.
        const message = detail && typeof detail === "object" ? (detail as { message?: unknown }).message : undefined;
        if (typeof message === "string" && message) return new Error(message);
    } catch {
        // не JSON — ниже отдадим текст как есть
    }
    return new Error(text || fallback);
};

export const getManualOnlyApi = async (args: ManualOnlyPairArgs): Promise<ManualOnlyState> => {
    const response = await fetch(`${editorBackendRoot()}/manual_only?${pairQuery(args)}`);
    if (!response.ok) throw await failure(response, "manual_only read failed");
    return normalizeManualOnlyState(await response.json());
};

/**
 * `streamAck` — переводчик подтвердил, что знает: у живого эфира пара с включённым режимом
 * перестаёт дублироваться (сервер редактора отказывает эфиру в подготовке и пересборке её папок).
 * Сервер без этого подтверждения включать режим эфиру не даёт. Лекции оно не нужно и не шлётся.
 */
export const setManualOnlyApi = async ({
    manualOnly,
    by,
    reason,
    streamAck,
    ...pair
}: ManualOnlyPairArgs & {
    manualOnly: boolean;
    by?: string;
    reason?: string;
    streamAck?: boolean;
}): Promise<ManualOnlyState> => {
    const response = await fetch(`${editorBackendRoot()}/manual_only?${pairQuery(pair)}`, {
        ...postOptions,
        body: JSON.stringify({
            manual_only: manualOnly,
            ...(by !== undefined ? { by } : {}),
            ...(reason !== undefined ? { reason } : {}),
            ...(streamAck ? { stream_ack: true } : {}),
        }),
    });
    if (!response.ok) throw await failure(response, "manual_only write failed");
    return normalizeManualOnlyState(await response.json());
};
